<template>
  <div class="documents-panel">

    <!-- ── Filter toggle bar ── -->
    <div class="filter-toggle-bar">
      <button class="filter-toggle-btn" type="button" @click="showFilters = !showFilters">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
        Filtros
        <span v-if="activeFilterCount > 0" class="filter-active-badge">{{ activeFilterCount }}</span>
        <svg class="filter-chevron" :class="{ open: showFilters }" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button v-if="activeFilterCount > 0" class="filter-clear-btn" type="button" @click="clearFilters">Limpar filtros</button>
    </div>

    <!-- ── Filters ── -->
    <Transition name="filter-bar">
      <div v-show="showFilters" class="filter-bar">
        <div class="filter-group">
          <label class="filter-label" for="docTypeFilter">Tipo</label>
          <select id="docTypeFilter" class="filter-select" :value="docFilterType" @change="$emit('set-doc-type', ($event.target as HTMLSelectElement).value)">
            <option value="">Todos os tipos</option>
            <option v-for="item in allTypes" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label" for="docProjectFilter">Projeto</label>
          <select id="docProjectFilter" class="filter-select" v-model="projectFilter">
            <option value="">Todos os projetos</option>
            <option v-for="proj in projectOptions" :key="proj" :value="proj">{{ proj }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label" for="docUserFilter">Autor</label>
          <select id="docUserFilter" class="filter-select" :value="docUserFilter" @change="$emit('set-doc-user', ($event.target as HTMLSelectElement).value)">
            <option value="">Todos os autores</option>
            <option v-for="item in docUserOptions" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label" for="docDateFrom">De</label>
          <input id="docDateFrom" class="filter-input" :value="docDateFrom" type="date" @input="$emit('set-doc-date-from', ($event.target as HTMLInputElement).value)" />
        </div>
        <div class="filter-group">
          <label class="filter-label" for="docDateTo">Até</label>
          <input id="docDateTo" class="filter-input" :value="docDateTo" type="date" @input="$emit('set-doc-date-to', ($event.target as HTMLInputElement).value)" />
        </div>
      </div>
    </Transition>

    <!-- ── Selection toolbar ── -->
    <Transition name="sel-bar">
      <div v-if="selectedDocs.length > 0" class="selection-bar">
        <span class="sel-count">{{ selectedDocs.length }} selecionado{{ selectedDocs.length > 1 ? 's' : '' }}</span>
        <div class="sel-actions">
          <button class="export-btn export-btn--doc" type="button" @click="openLang('doc')">
            <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><rect x="2" y="1" width="11" height="13" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5 5h5M5 7.5h5M5 10h3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
            Word
          </button>
          <button class="export-btn export-btn--pdf" type="button" @click="openLang('pdf')">
            <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><path d="M3 1.5h6l3 3V13a.5.5 0 01-.5.5H3A.5.5 0 012.5 13V2A.5.5 0 013 1.5z" stroke="currentColor" stroke-width="1.3"/><path d="M9 1.5V4.5h3M5 7h1.5c.828 0 1.5.448 1.5 1s-.672 1-1.5 1H5V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            PDF
          </button>
          <button class="export-btn export-btn--delete" type="button" @click="$emit('delete-docs')">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3V3.5M4.5 3.5v7.5h5V3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Apagar
          </button>
        </div>
      </div>
    </Transition>

    <!-- ── Skeleton ── -->
    <SkeletonList v-if="showSkeleton" variant="row" :count="5" :columns="[3, 40, 15, 10, 15]" />

    <!-- ── Empty ── -->
    <div v-else-if="displayedItems.length === 0" class="empty-state">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
      <p class="empty-title">Nenhum documento encontrado</p>
      <p class="empty-sub">Tente ajustar os filtros acima.</p>
    </div>

    <!-- ── List ── -->
    <div v-else class="doc-list">
      <div class="doc-list-header">
        <div class="col-check"></div>
        <div class="col-title">Documento</div>
        <div class="col-project">Projeto</div>
        <div class="col-date">Data</div>
        <div class="col-author">Autor</div>
        <div class="col-actions"></div>
      </div>

      <div
        v-for="item in displayedItems"
        :key="item.id"
        class="doc-row"
        :class="{ selected: selectedDocs.includes(item.id) }"
      >
        <div class="col-check">
          <input class="doc-checkbox" type="checkbox" :checked="selectedDocs.includes(item.id)" @change="$emit('toggle-doc', item.id)" />
        </div>
        <div class="col-title">
          <span class="type-badge" :data-type="item.type">{{ item.type || 'Doc' }}</span>
          <div class="doc-title-wrap">
            <button class="doc-title doc-title-link" type="button" @click="$emit('view', item.id)">{{ item.title }}</button>
            <span class="doc-date-mobile">{{ formatReportDate(item.reportDate) }}</span>
            <span v-if="activitiesText(item)" class="doc-preview">{{ activitiesText(item) }}</span>
          </div>
        </div>
        <div class="col-project">{{ item.attraction }}</div>
        <div class="col-date">{{ formatReportDate(item.reportDate) }}</div>
        <div class="col-author">
          <div class="author-avatar" :title="item.author.name">{{ item.author.name?.charAt(0)?.toUpperCase() }}</div>
          <span class="author-name">{{ item.author.name }}</span>
        </div>
        <div class="col-actions">
          <button class="row-link" type="button" @click="$emit('view', item.id)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            Ver
          </button>
          <button class="row-link" type="button" @click="$emit('edit', item.id)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Editar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Language Modal ── -->
    <Teleport to="body">
      <div v-if="showLangModal" class="lang-overlay" @click.self="showLangModal = false">
        <div class="lang-modal">
          <div class="lang-modal-header">
            <h3 class="lang-modal-title">
              {{ exportTarget === 'doc' ? 'Gerar documento Word (.docx)' : 'Gerar PDF' }}
            </h3>
            <p class="lang-modal-sub">Selecione o idioma do documento gerado</p>
          </div>
          <div class="lang-grid">
            <button v-for="loc in LANGUAGES" :key="loc.code" class="lang-option" type="button" @click="selectLang(loc.code)">
              <span class="lang-flag">{{ loc.flag }}</span>
              <span class="lang-label">{{ loc.label }}</span>
            </button>
          </div>
          <div class="lang-modal-footer">
            <button class="lang-cancel" type="button" @click="showLangModal = false">Cancelar</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, type PropType } from 'vue'
import { submissionsStore, type Submission } from '@/store/submissions'
import SkeletonList from '@/components/skeleton/SkeletonList.vue'

const LANGUAGES = [
  { code: 'pt', label: 'Português',  flag: '🇧🇷' },
  { code: 'en', label: 'English',    flag: '🇺🇸' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'it', label: 'Italiano',   flag: '🇮🇹' },
  { code: 'zh', label: '中文',        flag: '🇨🇳' },
  { code: 'ja', label: '日本語',      flag: '🇯🇵' },
  { code: 'ko', label: '한국어',      flag: '🇰🇷' },
  { code: 'ru', label: 'Русский',    flag: '🇷🇺' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
]

export default defineComponent({
  name: 'DocumentsPanel',
  components: { SkeletonList },
  props: {
    allTypes:           { type: Array as PropType<string[]>,    default: () => [] },
    docFilterType:      { type: String, default: '' },
    docUserFilter:      { type: String, default: '' },
    docDateFrom:        { type: String, default: '' },
    docDateTo:          { type: String, default: '' },
    docUserOptions:     { type: Array as PropType<string[]>,    default: () => [] },
    selectedDocs:       { type: Array as PropType<string[]>,    default: () => [] },
    documentsItems:     { type: Array as PropType<Submission[]>, default: () => [] },
    formatReportDate:   { type: Function as PropType<(v: string) => string>, required: true },
    activitiesText:     { type: Function as PropType<(item: Submission) => string>, required: true },
  },
  emits: ['set-doc-type','set-doc-user','set-doc-date-from','set-doc-date-to','export-docs','export-pdfs','delete-docs','toggle-doc','view','edit'],
  setup(props, { emit }) {
    const showLangModal = ref(false)
    const exportTarget  = ref<'doc' | 'pdf'>('doc')
    const showFilters   = ref(false)
    const projectFilter = ref('')

    const projectOptions = computed(() => {
      const s = new Set<string>()
      props.documentsItems.forEach(i => { if (i.attraction) s.add(i.attraction) })
      return Array.from(s).sort((a, b) => a.localeCompare(b))
    })

    const displayedItems = computed(() =>
      props.documentsItems.filter(i => !projectFilter.value || i.attraction === projectFilter.value)
    )

    const showSkeleton = computed(() => submissionsStore.state.loading && props.documentsItems.length === 0)

    const activeFilterCount = computed(() => {
      let n = 0
      if (props.docFilterType) n++
      if (props.docUserFilter) n++
      if (props.docDateFrom)   n++
      if (props.docDateTo)     n++
      if (projectFilter.value) n++
      return n
    })

    const clearFilters = () => {
      projectFilter.value = ''
      emit('set-doc-type', '')
      emit('set-doc-user', '')
      emit('set-doc-date-from', '')
      emit('set-doc-date-to', '')
    }

    const openLang = (target: 'doc' | 'pdf') => {
      exportTarget.value  = target
      showLangModal.value = true
    }

    const selectLang = (lang: string) => {
      showLangModal.value = false
      if (exportTarget.value === 'doc') emit('export-docs', lang)
      else                              emit('export-pdfs', lang)
    }

    return {
      showLangModal, exportTarget, openLang, selectLang, LANGUAGES,
      showFilters, projectFilter, projectOptions, displayedItems, activeFilterCount, clearFilters, showSkeleton
    }
  }
})
</script>

<style scoped lang="scss">
/* ── Filter toggle ── */
.filter-toggle-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.12);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted, #888);
  cursor: pointer;
  transition: background .12s, border-color .12s;
  &:hover { background: rgba(31,30,26,.04); border-color: rgba(31,30,26,.22); color: var(--ink, #2c2b27); }
}

.filter-active-badge {
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 1.4;
}

.filter-chevron {
  color: var(--ink-muted, #aaa);
  transition: transform .18s;
  &.open { transform: rotate(180deg); }
}

.filter-clear-btn {
  background: none;
  border: none;
  font-size: 12px;
  color: var(--system-navbar, #0f4c81);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: rgba(15,76,129,.06); }
}

/* ── Filter bar ── */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.filter-bar-enter-active, .filter-bar-leave-active { transition: opacity .18s ease, transform .18s ease; }
.filter-bar-enter-from, .filter-bar-leave-to { opacity: 0; transform: translateY(-6px); }
.filter-group { display: flex; flex-direction: column; gap: 4px; min-width: 140px; flex: 1; }
.filter-label { font-size: 11px; font-weight: 600; color: var(--ink-muted, #888); text-transform: uppercase; letter-spacing: .04em; }
.filter-select,
.filter-input {
  padding: 7px 10px; border-radius: 7px;
  border: 1.5px solid rgba(31,30,26,.15);
  font-size: 13px; color: var(--ink, #2c2b27); background: #fff;
  outline: none;
  transition: border-color .14s;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}

/* ── Selection toolbar ── */
.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  border-radius: 10px;
  padding: 10px 16px;
  margin-bottom: 12px;
}
.sel-count { font-size: 13px; font-weight: 600; opacity: .9; }
.sel-actions { display: flex; gap: 6px; flex-wrap: wrap; }

/* slide transition */
.sel-bar-enter-active,
.sel-bar-leave-active { transition: all .2s ease; }
.sel-bar-enter-from,
.sel-bar-leave-to { opacity: 0; transform: translateY(-6px); }

/* ── Export buttons ── */
.export-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 13px; border-radius: 7px; border: 1.5px solid transparent;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: opacity .14s, background .14s;
  white-space: nowrap;
  &--doc  { background: rgba(255,255,255,.15); color: #fff; border-color: rgba(255,255,255,.3); &:hover { background: rgba(255,255,255,.25); } }
  &--pdf  { background: rgba(255,255,255,.08); color: #fff; border-color: rgba(255,255,255,.2); &:hover { background: rgba(255,255,255,.18); } }
  &--delete { background: rgba(239,68,68,.15); color: #fca5a5; border-color: rgba(239,68,68,.35); &:hover { background: rgba(239,68,68,.25); } }
}

/* ── Empty ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; padding: 80px 20px; text-align: center;
  color: var(--ink-muted, #aaa);
  svg { opacity: .3; }
}
.empty-title { margin: 0; font-size: 16px; font-weight: 700; color: var(--ink, #2c2b27); }
.empty-sub   { margin: 0; font-size: 13px; }

/* ── Doc list ── */
.doc-list { width: 100%; }

.doc-list-header {
  display: grid;
  grid-template-columns: 32px 3fr 1.4fr 100px 1.2fr 120px;
  column-gap: 14px;
  padding: 8px 12px;
  font-size: 11px; font-weight: 700; color: var(--ink-muted, #888);
  text-transform: uppercase; letter-spacing: .05em;
  border-bottom: 1.5px solid rgba(31,30,26,.1);
  margin-bottom: 2px;
}

.doc-row {
  display: grid;
  grid-template-columns: 32px 3fr 1.4fr 100px 1.2fr 120px;
  column-gap: 14px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  border-bottom: 1px solid rgba(31,30,26,.06);
  transition: background .1s;
  &:hover { background: rgba(31,30,26,.025); }
  &.selected { background: rgba(37,99,235,.05); border-color: rgba(37,99,235,.12); }
}

.col-check { display: flex; align-items: center; justify-content: center; }
.doc-checkbox { width: 15px; height: 15px; accent-color: var(--system-navbar, #0f4c81); cursor: pointer; }

.col-title { display: flex; align-items: center; gap: 10px; min-width: 0; }
.type-badge {
  flex-shrink: 0;
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px;
  background: #e0f2fe; color: #0369a1;
  white-space: nowrap; text-transform: uppercase; letter-spacing: .04em;
  &[data-type="RDO"]   { background: #dcfce7; color: #15803d; }
  &[data-type="RDS"]   { background: #fef9c3; color: #854d0e; }
  &[data-type="Safety"] { background: #fef2f2; color: #dc2626; }
}
.doc-title-wrap { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.doc-title { font-size: 13px; font-weight: 500; color: var(--ink, #2c2b27); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.doc-title-link {
  background: none; border: none; padding: 0; cursor: pointer; text-align: left;
  &:hover { color: var(--system-navbar, #0f4c81); text-decoration: underline; }
}
.doc-preview { font-size: 11px; color: var(--ink-muted, #aaa); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.col-project { font-size: 12px; color: var(--ink-muted, #888); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.col-date    { font-size: 12px; color: var(--ink-muted, #888); white-space: nowrap; }

.col-author  { display: flex; align-items: center; gap: 7px; min-width: 0; }
.author-avatar {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  background: var(--system-navbar, #0f4c81); color: #fff;
  font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.author-name { font-size: 12px; color: var(--ink-muted, #888); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.col-actions { display: flex; gap: 4px; justify-content: flex-end; }
.row-link {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: none; cursor: pointer;
  font-size: 12px; font-weight: 500; color: var(--ink-muted, #666);
  padding: 5px 9px; border-radius: 6px;
  transition: background .1s, color .1s;
  &:hover { background: rgba(31,30,26,.06); color: var(--ink, #2c2b27); }
}

/* ── Language Modal ── */
.lang-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.lang-modal {
  background: var(--card, #fff);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0,0,0,.2);
  padding: 28px; width: 100%; max-width: 480px;
  display: flex; flex-direction: column; gap: 20px;
}
.lang-modal-header { text-align: center; }
.lang-modal-title { margin: 0 0 6px; font-size: 17px; font-weight: 700; color: var(--ink, #2c2b27); }
.lang-modal-sub { margin: 0; font-size: 13px; color: var(--ink-muted, #888); }
.lang-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; }
.lang-option {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 12px 8px; border-radius: 10px;
  border: 1.5px solid rgba(0,0,0,.1); background: var(--bg, #faf9f6);
  cursor: pointer; transition: border-color .14s, background .14s;
  &:hover { border-color: var(--accent, #2563eb); background: rgba(37,99,235,.05); }
}
.lang-flag  { font-size: 24px; line-height: 1; }
.lang-label { font-size: 11px; font-weight: 600; color: var(--ink, #2c2b27); }
.lang-modal-footer { display: flex; justify-content: center; }
.lang-cancel {
  padding: 8px 24px; border-radius: 7px; border: 1px solid rgba(0,0,0,.15);
  background: transparent; cursor: pointer;
  font-size: 13px; font-weight: 500; color: var(--ink-muted, #888);
  transition: background .12s;
  &:hover { background: rgba(0,0,0,.04); }
}

.doc-date-mobile {
  display: none;
  font-size: 11px;
  color: var(--ink-muted, #aaa);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 700px) {
  .doc-list-header { display: none; }
  .doc-row {
    grid-template-columns: 32px 1fr auto;
    grid-template-rows: auto auto;
    gap: 6px 10px;
    padding: 12px;
  }
  .col-project, .col-date, .col-author { display: none; }
  .col-actions { grid-column: 3; grid-row: 1; }
  .col-title { grid-column: 2; grid-row: 1 / 3; }
  .doc-date-mobile { display: block; }
  .doc-preview { display: none; }
}
</style>
